import { shareScreen, screenshareHasEnded, isSharing } from '/imports/ui/components/screenshare/service';
import { notify } from '/imports/ui/services/notification';
import logger from '/imports/startup/client/logger';
import Auth from '/imports/ui/services/auth';
import { defineMessages } from 'react-intl';
import { SET_RECORDING_STATUS } from '/imports/ui/components/recording/mutations';
import { SWAP_SCREENSHARE } from '../../core/graphql/mutations/userMutations';
import deviceInfo from '/imports/utils/deviceInfo';

const intlMessages = defineMessages({
  started: {
    id: 'app.autoScreenshare.started',
    description: 'Message shown when automatic screenshare starts',
  },
  failed: {
    id: 'app.autoScreenshare.failed',
    description: 'Message shown when automatic screenshare fails',
  },
  disabled: {
    id: 'app.autoScreenshare.disabled',
    description: 'Message shown when automatic screenshare is disabled',
  },
  recordingStarted: {
    id: 'app.autoScreenshare.recordingStarted',
    description: 'Message shown when automatic recording starts',
  },
  recordingStopped: {
    id: 'app.autoScreenshare.recordingStopped',
    description: 'Message shown when recording stops',
  },
  screenshareStopped: {
    id: 'app.autoScreenshare.screenshareStopped',
    description: 'Message shown when screenshare stops',
  },
  cancelled: {
    id: 'app.autoScreenshare.cancelled',
    description: 'Message shown when screenshare is cancelled',
  },
});

/**
 * AutoScreenshareService — singleton that manages the screenshare + recording
 * lifecycle for moderators (tutors).
 *
 * DESIGN NOTE: This service is a singleton. It assumes a single moderator per
 * meeting, which matches the LessonWise product model (1 tutor + 1 student).
 * If multi-moderator meetings are ever needed, the state must be scoped per
 * userId or replaced with per-user instances.
 */
class AutoScreenshareService {
  constructor() {
    this.enabled = true;
    this.hasStartedScreenshare = false;
    this.hasStartedRecording = false;
    this.apolloClient = null;
    this.recordingStatus = false;
    this.isRecordingToggleEnabled = true;
    this.isProcessingScreenshare = false;
    this.lastScreenshareAttempt = 0;
    this.screenshareCooldown = 5000; // 5 seconds cooldown between attempts
    this.recordingTiedToScreenshare = true;
    this.manualRecordingMode = true;

    // [Fix N1] Track join attempt in service (survives component remounts)
    this.hasAttemptedJoinScreenshare = false;

    // Modal state management
    this._isCancelModalOpen = false;
    this.cancelModalCallbacks = {
      onRetry: null,
      onCancel: null,
      onSkip: null,
    };
    this.currentUser = null;
    this.modalType = 'cancel';

    // [Fix Issue 4] Event-driven modal state listeners (replaces polling)
    this._modalListeners = new Set();

    // Skip state - when user skips initial screenshare prompt
    this.userSkippedInitialScreenshare = false;

    // Guard against rapid-fire manual start calls
    this._isProcessingManualStart = false;

    // [Fix Issue 2] Async mutex for screenshare start serialization
    this._screenshareLock = Promise.resolve();

    // [Fix Issue 3] Abort flag for rapid recording toggle
    this._abortPendingStart = false;

    // [Fix Issue 6] Screenshare dialog timeout (30 seconds)
    this._screenshareTimeoutMs = 30000;
  }

  // ---------------------------------------------------------------------------
  // Mutex for serializing screenshare start attempts [Fix Issue 2]
  // ---------------------------------------------------------------------------

  async _withScreenshareLock(fn) {
    const prev = this._screenshareLock;
    let release;
    this._screenshareLock = new Promise(resolve => { release = resolve; });
    try {
      await prev;
    } catch {
      // Previous lock holder may have thrown — that's fine, we proceed.
    }
    try {
      return await fn();
    } finally {
      release();
    }
  }

  // ---------------------------------------------------------------------------
  // Screenshare with timeout wrapper [Fix Issue 6]
  // ---------------------------------------------------------------------------

  async _shareScreenWithTimeout(...args) {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Screen capture dialog timed out')),
        this._screenshareTimeoutMs,
      );
    });
    try {
      return await Promise.race([shareScreen(...args), timeoutPromise]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // ---------------------------------------------------------------------------
  // Modal event emitter [Fix Issue 4]
  // ---------------------------------------------------------------------------

  _getModalSnapshot() {
    return {
      isOpen: this._isCancelModalOpen,
      callbacks: { ...this.cancelModalCallbacks },
      modalType: this.modalType,
      recordingStatus: this.recordingStatus,
    };
  }

  _notifyModalChange() {
    const snapshot = this._getModalSnapshot();
    this._modalListeners.forEach(fn => {
      try {
        fn(snapshot);
      } catch (e) {
        logger.error({
          logCode: 'screenshare_modal_listener_error',
          extraInfo: { errorMessage: e?.message },
        }, `Modal listener threw: ${e?.message}`);
      }
    });
  }

  /**
   * Subscribe to modal state changes. Returns an unsubscribe function.
   * The listener receives an atomic snapshot of all modal state.
   */
  onModalChange(fn) {
    this._modalListeners.add(fn);
    // Fire immediately so new subscribers get current state
    try { fn(this._getModalSnapshot()); } catch { /* ignore */ }
    return () => this._modalListeners.delete(fn);
  }

  // ---------------------------------------------------------------------------
  // Retry mutation helper
  // ---------------------------------------------------------------------------

  async retryMutation(mutationFn, operationName) {
    try {
      return await mutationFn();
    } catch (error) {
      logger.warn({
        logCode: `${operationName}_retry`,
        extraInfo: { errorName: error.name, errorMessage: error.message },
      }, `${operationName} failed, retrying in 1s: ${error.message}`);

      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        const result = await mutationFn();
        logger.info({
          logCode: `${operationName}_retry_success`,
        }, `${operationName} succeeded on retry`);
        return result;
      } catch (retryError) {
        logger.error({
          logCode: `${operationName}_retry_failed`,
          extraInfo: { errorName: retryError.name, errorMessage: retryError.message },
        }, `${operationName} failed after retry: ${retryError.message}`);
        throw retryError;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Apollo Client
  // ---------------------------------------------------------------------------

  setApolloClient(client) {
    this.apolloClient = client;
  }

  // ---------------------------------------------------------------------------
  // Enable / disable
  // ---------------------------------------------------------------------------

  setEnabled(enabled) {
    this.enabled = enabled;
    logger.info({
      logCode: 'auto_screenshare_setting_changed',
      extraInfo: { enabled },
    }, `Auto screenshare setting changed: ${enabled}`);
  }

  isEnabled() {
    return this.enabled;
  }

  // ---------------------------------------------------------------------------
  // Processing state [Fix N4] — expose for handler component
  // ---------------------------------------------------------------------------

  isProcessing() {
    return this.isProcessingScreenshare;
  }

  // ---------------------------------------------------------------------------
  // Modal state accessors
  // ---------------------------------------------------------------------------

  isCancelModalOpen() {
    return this._isCancelModalOpen;
  }

  setCancelModalOpen(isOpen) {
    this._isCancelModalOpen = isOpen;
    this._notifyModalChange();
  }

  getCancelModalCallbacks() {
    return this.cancelModalCallbacks;
  }

  getModalType() {
    return this.modalType;
  }

  getRecordingStatus() {
    return this.recordingStatus;
  }

  hasUserSkippedInitialScreenshare() {
    return this.userSkippedInitialScreenshare;
  }

  // ---------------------------------------------------------------------------
  // Show / hide modal [Fix Issue 5 — use this.currentUser in default callbacks]
  // ---------------------------------------------------------------------------

  showCancelModal(user, onRetry, onCancel, modalType = 'cancel', onSkip = null) {
    // [Fix remaining minor — user validation]
    if (!user || !user.userId) {
      logger.warn({
        logCode: 'screenshare_modal_no_user',
      }, '[SCREENSHARE FLOW] showCancelModal called without valid user');
      return;
    }

    this.currentUser = user;
    this.modalType = modalType;
    // [Fix Issue 5] Default callbacks read this.currentUser at invocation time,
    // not the captured `user` reference, to avoid stale closures.
    this.cancelModalCallbacks = {
      onRetry: onRetry || (() => this.retryScreenshare(this.currentUser)),
      onCancel: onCancel || (() => this.handleScreenshareCancelled(this.currentUser)),
      onSkip: onSkip || (() => this.handleSkipScreenshare(this.currentUser)),
    };
    this._isCancelModalOpen = true;
    this._notifyModalChange();

    logger.info({
      logCode: 'screenshare_modal_shown',
      extraInfo: {
        userId: user.userId,
        userName: user.name,
        modalType,
        isRecordingActive: this.recordingStatus,
        hasSkipOption: modalType === 'initial' || modalType === 'manualStop',
      },
    }, `[SCREENSHARE FLOW] Modal shown: type=${modalType}, recording=${this.recordingStatus}`);
  }

  hideCancelModal() {
    logger.debug({
      logCode: 'screenshare_modal_hidden',
      extraInfo: {
        previousModalType: this.modalType,
      },
    }, `[SCREENSHARE FLOW] Modal hidden`);

    this._isCancelModalOpen = false;
    this.cancelModalCallbacks = {
      onRetry: null,
      onCancel: null,
      onSkip: null,
    };
    this._notifyModalChange();
  }

  // ---------------------------------------------------------------------------
  // Skip / retry / cancel handlers
  // ---------------------------------------------------------------------------

  handleSkipScreenshare(user) {
    this.userSkippedInitialScreenshare = true;
    this.hideCancelModal();

    logger.info({
      logCode: 'screenshare_user_skipped',
      extraInfo: {
        userId: user?.userId,
        userName: user?.name,
        isRecordingActive: this.recordingStatus,
      },
    }, `[SCREENSHARE FLOW] User clicked "Skip for now": ${user?.name}`);
  }

  /**
   * Retry screenshare after cancellation.
   * [Fix Issue 7] Don't reset flags here — the called methods manage their own flags.
   */
  async retryScreenshare(user) {
    logger.info({
      logCode: 'screenshare_user_retry',
      extraInfo: {
        userId: user?.userId,
        userName: user?.name,
        isRecordingActive: this.recordingStatus,
        retryContext: this.recordingStatus ? 'recording' : 'initial',
      },
    }, `[SCREENSHARE FLOW] User clicked "Share Screen" (retry): ${user?.name}, recording=${this.recordingStatus}`);

    this.hideCancelModal();

    if (this.recordingStatus) {
      await this.startScreenshareForRecording(user);
    } else {
      await this.startScreenshareForModerator(user);
    }
  }

  handleScreenshareCancelled(user) {
    logger.info({
      logCode: 'screenshare_user_cancelled',
      extraInfo: {
        userId: user?.userId,
        userName: user?.name,
        isRecordingActive: this.recordingStatus,
      },
    }, `[SCREENSHARE FLOW] User cancelled screenshare: ${user?.name}`);

    this.hasStartedScreenshare = false;
    this.isProcessingScreenshare = false;
    this.lastScreenshareAttempt = 0;

    // [Fix N8] Reset layout swap on cancel
    this._swapScreenshareOff();

    this.hideCancelModal();
    notify(intlMessages.cancelled, 'info', 'desktop');
  }

  // ---------------------------------------------------------------------------
  // Cooldown check
  // ---------------------------------------------------------------------------

  shouldAttemptScreenshare() {
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastScreenshareAttempt;

    if (isSharing() || this.isProcessingScreenshare || timeSinceLastAttempt < this.screenshareCooldown) {
      logger.debug({
        logCode: 'auto_screenshare_skip_attempt',
        extraInfo: {
          isSharing: isSharing(),
          isProcessing: this.isProcessingScreenshare,
          timeSinceLastAttempt,
          cooldown: this.screenshareCooldown,
        },
      }, 'Skipping screenshare attempt due to existing activity or cooldown');
      return false;
    }

    return true;
  }

  // ---------------------------------------------------------------------------
  // User join handler
  // ---------------------------------------------------------------------------

  handleUserJoin(user) {
    // [Fix remaining minor — user validation]
    if (!user || !user.userId) {
      logger.warn({ logCode: 'screenshare_join_no_user' }, '[SCREENSHARE FLOW] handleUserJoin called without valid user');
      return;
    }

    if (!this.enabled) {
      logger.debug({
        logCode: 'screenshare_join_disabled',
        extraInfo: { userId: user.userId },
      }, `[SCREENSHARE FLOW] Auto-screenshare disabled, skipping`);
      return;
    }

    // Skip auto screenshare for iPad devices
    if (deviceInfo.isIos && deviceInfo.isTablet) {
      logger.info({
        logCode: 'screenshare_join_ipad_skip',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          deviceType: 'iPad',
        },
      }, `[SCREENSHARE FLOW] Skipped for iPad device: ${user.name}`);
      return;
    }

    if (!user.isModerator) {
      logger.debug({
        logCode: 'screenshare_join_not_moderator',
        extraInfo: { userId: user.userId, isModerator: user.isModerator },
      }, `[SCREENSHARE FLOW] User is not moderator, skipping`);
      return;
    }

    if (this.hasStartedScreenshare) {
      logger.debug({
        logCode: 'screenshare_join_already_started',
        extraInfo: { userId: user.userId },
      }, `[SCREENSHARE FLOW] Screenshare already started, skipping`);
      return;
    }

    // Only start screenshare if this is the current user (moderator joining)
    if (user.userId !== Auth.userID) {
      return;
    }

    if (!this.shouldAttemptScreenshare()) {
      return;
    }

    logger.info({
      logCode: 'screenshare_join_initiating',
      extraInfo: {
        userId: user.userId,
        userName: user.name,
        isModerator: user.isModerator,
        userSkippedBefore: this.userSkippedInitialScreenshare,
      },
    }, `[SCREENSHARE FLOW] Moderator joined, initiating screenshare prompt in 2s: ${user.name}`);

    // Start recording immediately on moderator join (before screenshare prompt)
    // This ensures the recording indicator appears right away
    this.startRecordingOnJoin(user);

    // Start screenshare after a short delay to ensure everything is loaded
    // [Fix remaining minor — try-catch in setTimeout]
    setTimeout(() => {
      try {
        this.startScreenshareForModerator(user);
      } catch (err) {
        logger.error({
          logCode: 'screenshare_join_timeout_error',
          extraInfo: { errorMessage: err?.message },
        }, `[SCREENSHARE FLOW] Error in delayed join handler: ${err?.message}`);
      }
    }, 2000);
  }

  // ---------------------------------------------------------------------------
  // Recording toggle handler
  // ---------------------------------------------------------------------------

  handleRecordingToggle(isRecording, user) {
    // [Fix remaining minor — user validation]
    if (!user || !user.userId) {
      logger.warn({ logCode: 'screenshare_recording_toggle_no_user' }, '[SCREENSHARE FLOW] handleRecordingToggle called without valid user');
      return;
    }

    const previousRecordingStatus = this.recordingStatus;

    logger.info({
      logCode: 'screenshare_recording_toggle',
      extraInfo: {
        userId: user.userId,
        userName: user.name,
        isRecording,
        previousRecordingStatus,
        isCurrentlySharing: isSharing(),
        manualRecordingMode: this.manualRecordingMode,
      },
    }, `[SCREENSHARE FLOW] Recording toggled: ${previousRecordingStatus} → ${isRecording}, isSharing=${isSharing()}`);

    if (!this.isRecordingToggleEnabled) {
      logger.debug({
        logCode: 'screenshare_recording_toggle_disabled',
        extraInfo: { userId: user.userId },
      }, `[SCREENSHARE FLOW] Recording toggle handling disabled`);
      return;
    }

    // Skip auto screenshare for iPad devices
    if (deviceInfo.isIos && deviceInfo.isTablet) {
      logger.info({
        logCode: 'screenshare_recording_ipad_skip',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          deviceType: 'iPad',
          isRecording,
        },
      }, `[SCREENSHARE FLOW] Recording toggle skipped for iPad: ${user.name}`);
      return;
    }

    this.recordingStatus = isRecording;

    // In manual recording mode, we handle recording resume but ignore manual recording start
    if (this.manualRecordingMode) {
      if (isRecording) {
        if (!isSharing()) {
          logger.info({
            logCode: 'auto_recording_manual_mode_resume',
            extraInfo: {
              userId: user.userId,
              userName: user.name,
              hasStartedRecording: this.hasStartedRecording,
            },
          }, `Recording resumed in manual mode, starting screenshare for moderator: ${user.name}`);
          // [Fix remaining minor — add shouldAttemptScreenshare guard]
          if (this.shouldAttemptScreenshare()) {
            this.startScreenshareForRecording(user);
          }
        } else {
          logger.info({
            logCode: 'auto_recording_manual_mode_resume_with_screenshare',
            extraInfo: {
              userId: user.userId,
              userName: user.name,
            },
          }, `Recording resumed in manual mode, screenshare already active for moderator: ${user.name}`);
          this.startRecordingAfterScreenshare(user);
        }
        return;
      } else {
        logger.info({
          logCode: 'auto_recording_manual_mode_stop_handled',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            hasStartedRecording: this.hasStartedRecording,
          },
        }, `Recording stopped/paused in manual mode, stopping screenshare for moderator: ${user.name}`);
        this.stopScreenshareForRecording(user);
        return;
      }
    }

    // If recording is tied to screenshare, only start recording when screenshare is active
    if (this.recordingTiedToScreenshare) {
      if (isRecording && isSharing()) {
        this.startRecordingAfterScreenshare(user);
      } else if (isRecording && !isSharing()) {
        if (this.shouldAttemptScreenshare()) {
          this.startScreenshareForRecording(user);
        }
      } else if (!isRecording) {
        logger.info({
          logCode: 'auto_recording_stopped_paused',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            hasStartedRecording: this.hasStartedRecording,
          },
        }, `Recording stopped/paused, stopping screenshare for moderator: ${user.name}`);
        this.stopScreenshareForRecording(user);
      }
    } else {
      if (isRecording) {
        if (!isSharing() && this.shouldAttemptScreenshare()) {
          this.startScreenshareForRecording(user);
        }
      } else {
        logger.info({
          logCode: 'auto_recording_stopped_paused_independent',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            hasStartedRecording: this.hasStartedRecording,
          },
        }, `Recording stopped/paused, stopping screenshare for moderator: ${user.name}`);
        this.stopScreenshareForRecording(user);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Manual start handler — wrapped with mutex [Fix Issue 2]
  // ---------------------------------------------------------------------------

  async handleScreenshareManualStart(user) {
    // [Fix remaining minor — user validation]
    if (!user || !user.userId) return;

    if (this._isProcessingManualStart) return;
    this._isProcessingManualStart = true;

    try {
      await this._withScreenshareLock(async () => {
        logger.info({
          logCode: 'screenshare_manual_start',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            isRecordingActive: this.recordingStatus,
            manualRecordingMode: this.manualRecordingMode,
            hasStartedRecording: this.hasStartedRecording,
          },
        }, `[SCREENSHARE FLOW] Manual screenshare started: ${user.name}, recording=${this.recordingStatus}`);

        // Start recording when screenshare is manually started
        if (this.manualRecordingMode && !this.hasStartedRecording) {
          await this.startRecordingAfterScreenshare(user);
        }

        // [Blip fix] Only fire SWAP if auto-screenshare didn't already handle it.
        // When hasStartedScreenshare is true, SWAP was already fired in
        // _startScreenshareForModeratorImpl or _startScreenshareForRecordingImpl.
        // This path only fires for truly manual screenshare (BBB toolbar button).
        if (this.apolloClient && !this.hasStartedScreenshare) {
          await this.retryMutation(
            () => this.apolloClient.mutate({
              mutation: SWAP_SCREENSHARE,
              variables: {
                screenshareAsContent: true,
              },
            }),
            'swap_screenshare'
          );
        }

        this.hasStartedScreenshare = true;

        logger.info({
          logCode: 'screenshare_manual_start_completed',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            hasStartedRecording: this.hasStartedRecording,
          },
        }, `[SCREENSHARE FLOW] Manual screenshare handling complete: ${user.name}`);
      });
    } catch (error) {
      logger.error({
        logCode: 'auto_screenshare_manual_start_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to handle manual screenshare start for moderator: ${error.message}`);
    } finally {
      this._isProcessingManualStart = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Manual stop handler [Fix N10 — check recordingStatus only, not isSharing()]
  // ---------------------------------------------------------------------------

  async handleScreenshareManualStop(user) {
    // [Fix remaining minor — user validation]
    if (!user || !user.userId) return;

    try {
      logger.info({
        logCode: 'screenshare_manual_stop',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          isRecordingActive: this.recordingStatus,
          isCurrentlySharing: isSharing(),
        },
      }, `[SCREENSHARE FLOW] Manual screenshare stop requested: ${user.name}, recording=${this.recordingStatus}`);

      // [Fix N10] Check recordingStatus only — isSharing() is already false by the
      // time the debounced handler calls this method.
      if (this.recordingStatus) {
        logger.warn({
          logCode: 'screenshare_stop_blocked_recording',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
          },
        }, `[SCREENSHARE FLOW] Stop blocked - recording is active: ${user.name}`);

        // [Fix #3] Recording is active — just restart screenshare directly.
        // Previously showed mandatoryStop modal + called retryScreenshare(),
        // but retryScreenshare() calls hideCancelModal() synchronously,
        // so the modal was shown and immediately hidden. Since this is mandatory
        // (no user choice), skip the modal entirely.
        this.retryScreenshare(user);
        return;
      }

      // Show cancel modal for manual screenshare stop (when not mandatory)
      this.showCancelModal(user,
        () => this.retryScreenshare(user),
        () => {
          this.hasStartedScreenshare = false;
          this.isProcessingScreenshare = false;
          this._swapScreenshareOff();
          this.hideCancelModal();
        },
        'manualStop'
      );

      logger.info({
        logCode: 'screenshare_manual_stop_modal_shown',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
        },
      }, `[SCREENSHARE FLOW] Manual stop modal shown: ${user.name}`);

    } catch (error) {
      logger.error({
        logCode: 'auto_screenshare_manual_stop_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to handle manual screenshare stop for moderator: ${error.message}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Start screenshare for moderator — wrapped with mutex [Fix Issue 2]
  // ---------------------------------------------------------------------------

  async startScreenshareForModerator(user) {
    return this._withScreenshareLock(() => this._startScreenshareForModeratorImpl(user));
  }

  async _startScreenshareForModeratorImpl(user) {
    try {
      if (deviceInfo.isIos && deviceInfo.isTablet) {
        logger.info({
          logCode: 'auto_screenshare_moderator_skipped_ipad',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            deviceType: 'iPad',
          },
        }, `Auto screenshare for moderator skipped for iPad device - user: ${user.name}`);
        return;
      }

      this.isProcessingScreenshare = true;
      this.lastScreenshareAttempt = Date.now();
      this._abortPendingStart = false; // [Fix Issue 3] Reset abort flag

      logger.info({
        logCode: 'screenshare_starting',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          context: 'moderator_join',
          isRecordingActive: this.recordingStatus,
        },
      }, `[SCREENSHARE FLOW] Starting screenshare prompt for moderator: ${user.name}`);

      const stopExternalVideoShare = () => {};

      const handleScreenshareFailure = (error) => {
        logger.warn({
          logCode: 'screenshare_attempt_failed',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            errorName: error?.name,
            errorMessage: error?.message,
            context: 'moderator_join',
          },
        }, `[SCREENSHARE FLOW] Attempt failed on join: ${error?.message || 'user cancelled'}`);

        this.isProcessingScreenshare = false;

        // [Fix remaining minor — try-catch in setTimeout]
        setTimeout(() => {
          try {
            const sharingStatus = isSharing();

            logger.debug({
              logCode: 'screenshare_failure_check',
              extraInfo: {
                userId: user.userId,
                isSharing: sharingStatus,
                willShowModal: !sharingStatus,
              },
            }, `[SCREENSHARE FLOW] Post-failure check: isSharing=${sharingStatus}`);

            if (!sharingStatus) {
              this.showCancelModal(
                user,
                () => this.retryScreenshare(this.currentUser),
                () => this.handleScreenshareCancelled(this.currentUser),
                'initial',
                () => this.handleSkipScreenshare(this.currentUser)
              );
            }
          } catch (err) {
            logger.error({
              logCode: 'screenshare_failure_check_error',
              extraInfo: { errorMessage: err?.message },
            }, `[SCREENSHARE FLOW] Error in failure check: ${err?.message}`);
          }
        }, 500);
      };

      // [Fix Issue 6] Use timeout wrapper
      // [Fix #1] Pass args positionally (not wrapped in array)
      await this._shareScreenWithTimeout(
        false, // isCameraAsContentBroadcasting
        stopExternalVideoShare,
        true, // isPresenter
        handleScreenshareFailure,
        {
          constraints: {
            video: {
              displaySurface: 'monitor',
              logicalSurface: true,
            },
            audio: true,
          },
        },
      );

      // [Fix Issue 3] Check abort flag after async shareScreen
      if (this._abortPendingStart) {
        logger.info({
          logCode: 'screenshare_aborted_after_start',
          extraInfo: { userId: user.userId },
        }, `[SCREENSHARE FLOW] Screenshare aborted (stop requested during start)`);
        this._abortPendingStart = false;
        screenshareHasEnded();
        return;
      }

      // [Blip fix] Fire SWAP_SCREENSHARE immediately after shareScreen resolves,
      // BEFORE React effects run. This prevents the "You are now sharing your screen"
      // presenter overlay from flashing — the layout already knows screenshare is
      // content by the time the sharing effect in AutoScreenshareHandler fires.
      // For truly manual screenshare (BBB toolbar button), hasStartedScreenshare
      // will be false, so handleScreenshareManualStart will fire SWAP as before.
      this.hasStartedScreenshare = true;
      if (this.apolloClient) {
        await this.retryMutation(
          () => this.apolloClient.mutate({
            mutation: SWAP_SCREENSHARE,
            variables: { screenshareAsContent: true },
          }),
          'swap_screenshare'
        );
      }

      // Close modal if it's still open
      // [Fix remaining minor — try-catch in setTimeout]
      setTimeout(() => {
        try {
          if (isSharing() && this._isCancelModalOpen) {
            logger.info({
              logCode: 'screenshare_success_modal_close',
              extraInfo: {
                userId: user.userId,
                context: 'moderator_join',
              },
            }, `[SCREENSHARE FLOW] Screenshare succeeded, closing modal`);
            this.hideCancelModal();
          }
        } catch (err) {
          logger.error({
            logCode: 'screenshare_success_check_error',
            extraInfo: { errorMessage: err?.message },
          }, `[SCREENSHARE FLOW] Error in success check: ${err?.message}`);
        }
      }, 200);

      logger.info({
        logCode: 'screenshare_started',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          context: 'moderator_join',
          isRecordingActive: this.recordingStatus,
          willStartRecording: !this.manualRecordingMode && this.recordingTiedToScreenshare && this.recordingStatus,
        },
      }, `[SCREENSHARE FLOW] Screenshare started successfully: ${user.name}`);

      // In manual mode, don't automatically start recording
      if (!this.manualRecordingMode && this.recordingTiedToScreenshare && this.recordingStatus) {
        this.startRecordingAfterScreenshare(user);
      }

    } catch (error) {
      logger.error({
        logCode: 'auto_screenshare_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to start automatic screenshare for moderator: ${error.message}`);
    } finally {
      this.isProcessingScreenshare = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Start screenshare for recording — wrapped with mutex [Fix Issue 2]
  // ---------------------------------------------------------------------------

  async startScreenshareForRecording(user) {
    return this._withScreenshareLock(() => this._startScreenshareForRecordingImpl(user));
  }

  async _startScreenshareForRecordingImpl(user) {
    try {
      if (deviceInfo.isIos && deviceInfo.isTablet) {
        logger.info({
          logCode: 'auto_screenshare_recording_method_skipped_ipad',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            deviceType: 'iPad',
          },
        }, `Auto screenshare for recording method skipped for iPad device - user: ${user.name}`);
        return;
      }

      this.isProcessingScreenshare = true;
      this.lastScreenshareAttempt = Date.now();
      this._abortPendingStart = false; // [Fix Issue 3] Reset abort flag

      logger.info({
        logCode: 'screenshare_recording_start',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          context: 'recording_start',
          isCurrentlySharing: isSharing(),
        },
      }, `[SCREENSHARE FLOW] Starting screenshare for recording: ${user.name}`);

      const handleScreenshareFailure = (error) => {
        logger.warn({
          logCode: 'auto_screenshare_recording_cancelled',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
            errorName: error?.name,
            errorMessage: error?.message,
          },
        }, `Screenshare cancelled or failed for recording, not starting recording for moderator: ${user.name}`);

        this.isProcessingScreenshare = false;

        // [Fix remaining minor — try-catch in setTimeout]
        setTimeout(() => {
          try {
            const sharingStatus = isSharing();

            logger.debug({
              logCode: 'screenshare_recording_failure_check',
              extraInfo: {
                userId: user.userId,
                isSharing: sharingStatus,
                willShowModal: !sharingStatus,
              },
            }, `[SCREENSHARE FLOW] Post-failure check (recording): isSharing=${sharingStatus}`);

            if (!sharingStatus) {
              this.showCancelModal(
                user,
                () => this.retryScreenshare(this.currentUser),
                () => this.handleScreenshareCancelled(this.currentUser),
                'recording',
                null
              );
            }
          } catch (err) {
            logger.error({
              logCode: 'screenshare_recording_failure_check_error',
              extraInfo: { errorMessage: err?.message },
            }, `[SCREENSHARE FLOW] Error in recording failure check: ${err?.message}`);
          }
        }, 500);
      };

      // [Fix Issue 6] Use timeout wrapper
      // [Fix #1] Pass args positionally (not wrapped in array)
      await this._shareScreenWithTimeout(
        false,
        () => { },
        true,
        handleScreenshareFailure,
        {
          constraints: {
            video: {
              displaySurface: 'monitor',
              logicalSurface: true,
            },
            audio: true,
          },
        },
      );

      // [Fix Issue 3] Check abort flag after async shareScreen
      if (this._abortPendingStart) {
        logger.info({
          logCode: 'screenshare_recording_aborted_after_start',
          extraInfo: { userId: user.userId },
        }, `[SCREENSHARE FLOW] Screenshare for recording aborted (stop requested during start)`);
        this._abortPendingStart = false;
        screenshareHasEnded();
        return;
      }

      // Only proceed if screenshare is actually active (user didn't cancel)
      if (!isSharing()) {
        logger.warn({
          logCode: 'auto_screenshare_recording_not_active',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
          },
        }, `Screenshare not active after share attempt, not starting recording for moderator: ${user.name}`);
        return;
      }

      // [Blip fix] Fire SWAP_SCREENSHARE immediately after shareScreen resolves,
      // BEFORE React effects run. Same rationale as _startScreenshareForModeratorImpl.
      this.hasStartedScreenshare = true;
      if (this.apolloClient) {
        await this.retryMutation(
          () => this.apolloClient.mutate({
            mutation: SWAP_SCREENSHARE,
            variables: { screenshareAsContent: true },
          }),
          'swap_screenshare'
        );
      }

      // Close modal if it's still open
      // [Fix remaining minor — try-catch in setTimeout]
      setTimeout(() => {
        try {
          if (isSharing() && this._isCancelModalOpen) {
            logger.info({
              logCode: 'screenshare_recording_success_modal_close',
              extraInfo: {
                userId: user.userId,
                context: 'recording_start',
              },
            }, `[SCREENSHARE FLOW] Screenshare succeeded for recording, closing modal`);
            this.hideCancelModal();
          }
        } catch (err) {
          logger.error({
            logCode: 'screenshare_recording_success_check_error',
            extraInfo: { errorMessage: err?.message },
          }, `[SCREENSHARE FLOW] Error in recording success check: ${err?.message}`);
        }
      }, 200);

      logger.info({
        logCode: 'screenshare_recording_started',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          willStartRecording: this.recordingStatus,
        },
      }, `[SCREENSHARE FLOW] Screenshare active for recording: ${user.name}`);

      // Start recording after screenshare is successful
      if (this.recordingStatus) {
        logger.info({
          logCode: 'auto_recording_starting_after_screenshare',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
          },
        }, `Starting recording after successful screenshare for moderator: ${user.name}`);
        this.startRecordingAfterScreenshare(user);
      }

    } catch (error) {
      logger.error({
        logCode: 'auto_screenshare_recording_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to start screenshare for recording: ${error.message}`);
    } finally {
      this.isProcessingScreenshare = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Stop screenshare for recording [Fix N8 — send SWAP_SCREENSHARE(false)]
  // ---------------------------------------------------------------------------

  stopScreenshareForRecording(user) {
    try {
      // [Fix Issue 3] Signal abort to any pending start
      this._abortPendingStart = true;

      if (!isSharing()) {
        logger.debug({
          logCode: 'auto_screenshare_not_sharing',
          extraInfo: { userId: user.userId },
        }, 'Screenshare not active, skipping stop');
        return;
      }

      logger.info({
        logCode: 'screenshare_stopping_for_recording',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          hasStartedRecording: this.hasStartedRecording,
        },
      }, `[SCREENSHARE FLOW] Stopping screenshare (recording stopped): ${user.name}`);

      screenshareHasEnded();

      this.hasStartedScreenshare = false;

      // [Fix N8] Reset layout swap
      this._swapScreenshareOff();

      // Stop recording when screenshare stops
      if (this.hasStartedRecording) {
        logger.info({
          logCode: 'auto_recording_stopping_after_screenshare',
          extraInfo: {
            userId: user.userId,
            userName: user.name,
          },
        }, `Stopping recording after screenshare stop for moderator: ${user.name}`);
        this.stopRecordingAfterScreenshare(user);
      }

      logger.info({
        logCode: 'screenshare_stopped_for_recording',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
        },
      }, `[SCREENSHARE FLOW] Screenshare stopped (recording ended): ${user.name}`);

    } catch (error) {
      logger.error({
        logCode: 'auto_screenshare_recording_stop_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to stop screenshare for recording: ${error.message}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Helper: send SWAP_SCREENSHARE(false) [Fix N8]
  // ---------------------------------------------------------------------------

  _swapScreenshareOff() {
    if (this.apolloClient) {
      this.retryMutation(
        () => this.apolloClient.mutate({
          mutation: SWAP_SCREENSHARE,
          variables: { screenshareAsContent: false },
        }),
        'swap_screenshare_off'
      ).catch((err) => {
        logger.warn({
          logCode: 'swap_screenshare_off_failed',
          extraInfo: { errorMessage: err?.message },
        }, `[SCREENSHARE FLOW] Failed to reset screenshare layout: ${err?.message}`);
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Start recording on moderator join (before screenshare)
  // ---------------------------------------------------------------------------

  async startRecordingOnJoin(user) {
    if (!user || !user.userId || !user.isModerator) return;
    if (this.hasStartedRecording) return;
    if (!this.apolloClient) {
      logger.warn({ logCode: 'recording_join_no_apollo' }, 'Apollo Client not available');
      return;
    }

    logger.info({
      logCode: 'recording_starting_on_join',
      extraInfo: { userId: user.userId, userName: user.name },
    }, `[RECORDING] Starting recording on moderator join: ${user.name}`);

    try {
      await this.retryMutation(
        () => this.apolloClient.mutate({
          mutation: SET_RECORDING_STATUS,
          variables: { recording: true },
        }),
        'start_recording_on_join'
      );
      this.hasStartedRecording = true;
      this.recordingStatus = true;
      logger.info({
        logCode: 'recording_started_on_join',
        extraInfo: { userId: user.userId },
      }, `[RECORDING] Recording started on join: ${user.name}`);
    } catch (error) {
      logger.error({
        logCode: 'recording_join_failed',
        extraInfo: { errorMessage: error.message },
      }, `Failed to start recording on join: ${error.message}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Recording start/stop mutations
  // ---------------------------------------------------------------------------

  async startRecordingAfterScreenshare(user) {
    try {
      if (this.hasStartedRecording) {
        logger.debug({
          logCode: 'auto_recording_already_started',
          extraInfo: { userId: user.userId },
        }, 'Auto recording already started, skipping');
        return;
      }

      if (!this.apolloClient) {
        logger.warn({
          logCode: 'auto_recording_no_apollo_client',
          extraInfo: { userId: user.userId },
        }, 'Apollo Client not available for recording');
        return;
      }

      logger.info({
        logCode: 'recording_starting',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          isScreenshareActive: isSharing(),
        },
      }, `[SCREENSHARE FLOW] Starting recording (screenshare active): ${user.name}`);

      await this.retryMutation(
        () => this.apolloClient.mutate({
          mutation: SET_RECORDING_STATUS,
          variables: {
            recording: true,
          },
        }),
        'start_recording'
      );

      this.hasStartedRecording = true;

      logger.info({
        logCode: 'recording_started',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
        },
      }, `[SCREENSHARE FLOW] Recording started successfully: ${user.name}`);

    } catch (error) {
      logger.error({
        logCode: 'auto_recording_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to start automatic recording for moderator: ${error.message}`);
    }
  }

  async stopRecordingAfterScreenshare(user) {
    try {
      if (!this.hasStartedRecording) {
        logger.debug({
          logCode: 'auto_recording_not_started',
          extraInfo: { userId: user.userId },
        }, 'Auto recording not started, skipping stop');
        return;
      }

      if (!this.apolloClient) {
        logger.warn({
          logCode: 'auto_recording_no_apollo_client',
          extraInfo: { userId: user.userId },
        }, 'Apollo Client not available for recording');
        return;
      }

      logger.info({
        logCode: 'recording_stopping',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
          isScreenshareActive: isSharing(),
        },
      }, `[SCREENSHARE FLOW] Stopping recording: ${user.name}`);

      await this.retryMutation(
        () => this.apolloClient.mutate({
          mutation: SET_RECORDING_STATUS,
          variables: {
            recording: false,
          },
        }),
        'stop_recording'
      );

      this.hasStartedRecording = false;

      logger.info({
        logCode: 'recording_stopped',
        extraInfo: {
          userId: user.userId,
          userName: user.name,
        },
      }, `[SCREENSHARE FLOW] Recording stopped successfully: ${user.name}`);

    } catch (error) {
      logger.error({
        logCode: 'auto_recording_stop_failed',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          userId: user.userId,
          userName: user.name,
        },
      }, `Failed to stop automatic recording for moderator: ${error.message}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Generic failure handler
  // ---------------------------------------------------------------------------

  handleScreenshareFailure(error) {
    logger.error({
      logCode: 'auto_screenshare_failure',
      extraInfo: {
        errorName: error.name,
        errorMessage: error.message,
      },
    }, `Automatic screenshare failed: ${error.message}`);
  }

  // ---------------------------------------------------------------------------
  // Reset [Fix N1 — also reset hasAttemptedJoinScreenshare]
  // ---------------------------------------------------------------------------

  reset() {
    logger.info({
      logCode: 'screenshare_service_reset',
      extraInfo: {
        previousState: {
          hasStartedScreenshare: this.hasStartedScreenshare,
          hasStartedRecording: this.hasStartedRecording,
          recordingStatus: this.recordingStatus,
          userSkippedInitialScreenshare: this.userSkippedInitialScreenshare,
        },
      },
    }, `[SCREENSHARE FLOW] Service reset (meeting ended or user left)`);

    this.hasStartedScreenshare = false;
    this.hasStartedRecording = false;
    this.recordingStatus = false;
    this.isProcessingScreenshare = false;
    this.lastScreenshareAttempt = 0;
    this._abortPendingStart = false;

    // [Fix N1] Reset join attempt tracking
    this.hasAttemptedJoinScreenshare = false;

    // Reset skip state
    this.userSkippedInitialScreenshare = false;
    this._isProcessingManualStart = false;

    // Reset modal state
    this._isCancelModalOpen = false;
    this.cancelModalCallbacks = {
      onRetry: null,
      onCancel: null,
      onSkip: null,
    };
    this.currentUser = null;
    this.modalType = 'cancel';
    // [Fix #7] Clear modal listeners to prevent stale references after reset
    this._modalListeners.clear();
    this._notifyModalChange();
  }
}

// Create singleton instance
const autoScreenshareService = new AutoScreenshareService();

export default autoScreenshareService;
